import { h, Component } from 'preact'
import Header from '../../header'
import AlertComponent from '../../ui-components/AlertComponent'
import Panel from '../../panel'
import InputGroup from '../../ui-components/InputGroup'
import Permissions from './Permissions'
import CategoryType from './CategoryType'
import IncludeForStats from './IncludeForStats'
import Attachments from './Attachments'
import ConfirmationModal from '../../ui-components/ConfirmationModal'
import CategoryContentCurationManager from '../../content-curation/CategoryContentCurationManager'
import actionTracker from './actionTracker'
import { FeaturedTopic } from '../../featured-topics/FeaturedTopics'
import SeoSettings, {MetaRobotsType} from './SeoSettings'
import ButtonControl from '../../button-control'
import ChangeParentCategoryModal from './ChangeParentCategoryModal'
import axios from 'axios'

const NEWS_CATEGORY_TYPE = 3

type AccessRuleType = 'main' | 'custom'

interface AccessRule {
  role: string,
  title: string,
  visibility: boolean,
  create_topics: boolean,
  create_posts: boolean
}

type ParentCategory = {
  id: number,
  title: string
}

type AccessRules = {
  [key in AccessRuleType]: Array<AccessRule>
}

interface ImageData {
  source_thumb: string,
  source: string,
  type: string,
  bbcode_url:string,
  short_url: string,
  short_thumb_url: string
}

interface ContentCuration {
  curatedTopics: Array<FeaturedTopic>,
  contentCurationTitle: string,
  title: string,
  enabled: boolean,
  enabledCommunityList: boolean,
  content: Array<any>
}

interface Category {
  id: number,
  parentId: number,
  includeForStats: boolean,
  type: number,
  title: string,
  description: string,
  heroImage?: ImageData,
  thumbnailImage?: ImageData,
  visibleSubCategories: number,
  visibleTopics: number,
  showDescriptionOnHomepage: boolean,
  hideFromSearch?: boolean,
  contentCuration: ContentCuration,
  seoTitle: string,
  seoDescription: string,
  permissions?: any,
  metaRobots?: MetaRobotsType
}

interface Props {
  category: Category,
  isKnowledgeBase: boolean,
  pageTitle: string,
  roles: AccessRules,
  newsAccessRules: AccessRules,
  overviewLink: string,
  parentCategories: []
}

interface State {
  alertMessage: string | null,
  messageType: string | null,
  errors: Array<string>,
  category: Category,
  showModal: boolean,
  showConfirmation: boolean,
  isLoading: boolean,
  showError: boolean,
  successMessage: string,
  showChangeParentCategoryModal: boolean,
  parentCategories: ParentCategory[],
  selectedParentCategory?: string,
  changeParentCategoryError?: string
}


class CategoryForm extends Component<Props, State> {
  disableCreatePermissionsList: Array<string>
  constructor (props: Props) {
    super()
    props.category.contentCuration.title = props.category.contentCuration.contentCurationTitle
    this.state = {
      alertMessage: null,
      messageType: null,
      errors: [],
      category: props.category,
      showModal: false,
      showConfirmation: false,
      isLoading: false,
      showError: false,
      successMessage: '',
      parentCategories: props.parentCategories,
      showChangeParentCategoryModal: false
    }
    this.saveCategory = this.saveCategory.bind(this)
    this.setValue = this.setValue.bind(this)
    this.isParentCategory = this.isParentCategory.bind(this)
    this.disableCreatePermissionsList = ['roles.not-activated', 'roles.requires-approval', 'roles.guest', 'roles.banned']
    this.deleteCategory = this.deleteCategory.bind(this)
    this.getDeleteConfirmationTitle = this.getDeleteConfirmationTitle.bind(this)
    this.getDeleteConfirmationText = this.getDeleteConfirmationText.bind(this)
    this.getDeleteConfirmationButtons = this.getDeleteConfirmationButtons.bind(this)
    this.closeConfirmationDialog = this.closeConfirmationDialog.bind(this)
    this.updatePermissions = this.updatePermissions.bind(this)
    this.changeParentCategory = this.changeParentCategory.bind(this)
  }

  saveCategory () {
    const newData = JSON.parse(JSON.stringify(this.state.category))
    let errors = []

    if (!newData.title || newData.title === '') {
      errors.push('title')
    }
    if (!newData.description || newData.description === '') {
      errors.push('description')
    }
    if (errors.length) {
      this.setState({ errors })
      return
    }
    newData.contentCuration.curatedTopics = this.state.category.contentCuration.curatedTopics.map(t => t.id)
    newData.contentCuration.contentCurationTitle = this.state.category.contentCuration.title
    this.request('POST', newData, () => {
      if (newData.parentId) {
        actionTracker(newData)
      }

      this.setState({ messageType: 'success', alertMessage: null })
      window.location.assign(this.props.overviewLink)
    }, () => {
      this.setState({ messageType: 'error', alertMessage: null })
    })
  }

  request (method: 'POST' | 'DELETE', data: Object, onSuccess: () => void, onFailure: () => void) {
    this.setState({ isLoading: true })
    const xhr = new window.XMLHttpRequest()

    xhr.onreadystatechange = (evt: any) => {
      if (xhr.readyState === 4) {
        if (evt.target.status >= 200 && evt.target.status < 400) {
          onSuccess()
        } else {
          onFailure()
        }
      }
      this.setState({ isLoading: false })
    }
    xhr.open(method, window.location.href, true)
    xhr.send(JSON.stringify(data))
  }

  deleteCategory () {
    this.request('DELETE', { id: this.state.category.id }, () => {
      this.setState({ messageType: 'success' })
      window.location.assign(this.props.overviewLink)
    }, () => {
      this.setState({ messageType: 'error' })
    })
  }

  isParentCategory () {
    return this.state.category.parentId === null
  }

  isNew () {
    return this.state.category.id === null
  }

  setValue (key: string, value: any) {
    const category = Object.assign({}, this.state.category, { [key]: value })
    this.setState({ category, errors: [] })

    if (key === 'type') {
      if (category.type === NEWS_CATEGORY_TYPE) {
        this.updatePermissions(this.props.newsAccessRules)
      } else {
        this.updatePermissions(this.props.roles)
      }
    }
  }

  componentDidMount () {
    this.updatePermissions(this.props.roles)
  }

  updatePermissions (accessRules: AccessRules) {
    const mainList = [].concat(accessRules.main as Array<never>)
    const updatedMainList = mainList.reverse().reduce((accumulator: any, currentValue: { role: string; create_topics: boolean; create_posts: boolean }) => {
      if (this.disableCreatePermissionsList.indexOf(currentValue.role) !== -1) {
        currentValue.create_topics = false
        currentValue.create_posts = false
      }
      return [currentValue].concat(accumulator)
    }, [])

    const concatList = [].concat(updatedMainList as Array<never>).concat(accessRules.custom as Array<never>)
    this.setValue('permissions', concatList)
  }

  closeConfirmationDialog () {
    this.setState({ showConfirmation: false, showError: false })
  }

  canBeDeleted () {
    return this.state.category.visibleSubCategories === 0 && this.state.category.visibleTopics === 0
  }

  getDeleteConfirmationTitle () {
    if (this.canBeDeleted()) {
      return `Delete ${this.isParentCategory() ? 'parent' : ''} category "${this.state.category.title}"`
    }

    return `${this.isParentCategory() ? 'Parent category' : 'Category'} is not empty yet`
  }

  getDeleteConfirmationText () {
    if (this.canBeDeleted()) {
      return "Be careful, once it's deleted, it's gone for good!"
    }

    if (this.isParentCategory()) {
      return <div>
        If you're absolutely sure you want to delete it, you'll need to remove all of its <strong>{this.state.category.visibleSubCategories} categories</strong> by going to <a href={this.props.overviewLink}>{this.props.isKnowledgeBase ? 'Knowledge Base' : 'Category Structure'}</a>, first.
      </div>
    }

    return <div>
      If you're absolutely sure you want to delete it, you'll need to move all of its <strong>{this.state.category.visibleTopics} topics</strong> a different category or delete them.
    </div>
  }

  getDeleteConfirmationButtons () {
    if (!this.canBeDeleted()) {
      return [{
        text: 'Okay',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-primary',
        showLoading: false
      }]
    }
    return [
      {
        text: 'Delete',
        callback: this.deleteCategory,
        class: 'btn btn-danger',
        showLoading: true
      },
      {
        text: 'Cancel',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-default',
        showLoading: false
      }
    ]
  }

  async changeParentCategory () {
    const { category, parentCategories, selectedParentCategory } = this.state
    const newParentCategory = parentCategories.find(parentCategory => parentCategory.title === selectedParentCategory)

    if (!selectedParentCategory || !newParentCategory) {
      this.setState({ changeParentCategoryError: 'Please select a parent category.' })
      return
    }

    this.setState({ isLoading: true })

    try {
      const res = await axios.post(
        '/settings/category/changeParentCategory',
        {
          categoryId: category.id,
          newParentCategoryId: newParentCategory.id
        })

      this.setState({
        alertMessage: 'The parent category was changed successfully.',
        changeParentCategoryError: undefined,
        isLoading: false,
        messageType: 'success',
        parentCategories: res.data.parentCategories,
        selectedParentCategory: undefined,
        showChangeParentCategoryModal: false
      })
    } catch (error) {
      this.setState({
        alertMessage: null,
        isLoading: false,
        messageType: 'error',
        showChangeParentCategoryModal: false
      })
    }
  }

  render (props: Props, state: State) {
    return (
      <div className='category-add_container'>
        <Header
          title={props.pageTitle}
          disableCancelButton
          disableSaveButton
          customButtons={[<div className={'btn btn-cancel js-cancel'} onClick={() => { window.location.assign(props.overviewLink) }}>
            Cancel
          </div>, ' ', this.isNew() ? null : <div className={'btn btn-danger js-delete'} onClick={() => this.setState({ showConfirmation: true })}>
            Delete
          </div>, ' ', <div className={'btn btn-success js-submit'} onClick={() => this.saveCategory()}>
            Save
          </div>]}
        />
        <AlertComponent
          messageType={state.messageType}
          message={state.alertMessage}
          sticky
        />
        <Panel title='Details'>
          <form>
            <InputGroup
              title='Title'
              placeholder='Add a title'
              type={'text'}
              error={state.errors.indexOf('title') !== -1 ? 'Please fill in this field' : ''}
              onChange={(e) => this.setValue('title', e.target.value)}
              value={state.category.title} />
            <InputGroup
              title='Description'
              placeholder='Add a description'
              error={state.errors.indexOf('description') !== -1 ? 'Please fill in this field' : ''}
              type={'text'}
              onChange={(e) => this.setValue('description', e.target.value)}
              value={state.category.description} />
            <SeoSettings
              seoTitle={state.category.seoTitle || state.category.title}
              seoDescription={state.category.seoDescription || state.category.description}
              metaRobots={state.category.metaRobots || 'index, follow'}
              hideFromSearch={state.category.hideFromSearch === undefined ? false : state.category.hideFromSearch}
              displayRobotsSettings={!this.isParentCategory() && !this.isNew()}
              onChangeHideFromSearch={(value: boolean) => this.setValue('hideFromSearch', value)}
              onChangeTitle={(value: string) => this.setValue('seoTitle', value)}
              onChangeDescription={(value: string) => this.setValue('seoDescription', value)}
              onChangeMetaRobot={(value: string) => this.setValue('metaRobots', value)}
              />
            {
              !this.isParentCategory() ? (
                <IncludeForStats
                  enable={state.category.includeForStats}
                  onUpdate={(value: boolean) => this.setValue('includeForStats', value)}
                />
              ) : null
            }
            {
              !props.isKnowledgeBase && !this.isParentCategory() ? (
                <CategoryType
                  currentType={state.category.type}
                  onUpdate={(value: number) => this.setValue('type', value)}
                />
              ) : null
            }
            {
              !props.isKnowledgeBase && this.isParentCategory() ? (
                <InputGroup
                  title='Show description in category list on homepage.'
                  type='checkbox'
                  id='show_description'
                  name='show_description'
                  checked={state.category.showDescriptionOnHomepage}
                  onChange={() => this.setValue('showDescriptionOnHomepage', !state.category.showDescriptionOnHomepage)}
                  formGroupClasses={`show_description ${props.isKnowledgeBase ? 'is-hidden' : ''}`}
                  inline />
              ) : null
            }
            <Attachments
              label='Hero image'
              className='hero-image'
              meta={
                <div>
                  <div>Hero image will be shown on top of the category page.</div>
                  <div>Maximum file size: 5MB. Allowed image types: .jpg, .png, .gif. We recommend a minimum of 2880*480px</div>
                </div>
              }
              isAttachmentSelected={state.category.heroImage}
              imageSrc={state.category.heroImage && state.category.heroImage.source_thumb}
              width={2880}
              height={480}
              onUpdate={(data: ImageData) => this.setValue('heroImage', data)}
            />
            <Attachments
              label='Thumbnail image'
              className='thumbnail-image'
              meta={
                <div>
                  <div>Allowed image types: .jpg, .png, .gif. We recommend a minimum of 144*144px</div>
                </div>
              }
              isAttachmentSelected={state.category.thumbnailImage}
              imageSrc={state.category.thumbnailImage && state.category.thumbnailImage.source_thumb}
              width={144}
              height={144}
              onUpdate={(data: ImageData) => this.setValue('thumbnailImage', data)}
            />
            {
              props.isKnowledgeBase && this.isParentCategory() ? (
                <Attachments
                  label='Category card image'
                  className='thumbnail-image'
                  meta={
                    <div>
                      <div>Category card image will be shown on category cards on homepage.</div>
                      <div>Maximum file size: 5MB. Allowed image types: .jpg, .png. We recommend a minimum of 720*272px</div>
                    </div>
                  }
                  isAttachmentSelected={state.category.thumbnailImage}
                  imageSrc={state.category.thumbnailImage && state.category.thumbnailImage.source_thumb}
                  width={720}
                  height={272}
                  onUpdate={(data: ImageData) => this.setValue('thumbnailImage', data)}
                />
              ) : null
            }
          </form>
        </Panel>
        {
          !props.isKnowledgeBase && !this.isParentCategory() ? (
            <Panel title='Curated content'>
              <CategoryContentCurationManager
                description='Display a list of curated content, such as articles, tutorials and guides to help users find their answers quickly.'
                contentCuration={state.category.contentCuration}
                onChange={(value: ContentCuration) => this.setValue('contentCuration', { ...state.category.contentCuration, ...value })}
              />
            </Panel>
          ) : null
        }
        {
          !this.isParentCategory() && (
            <Panel title='Permissions'>
              <Permissions
                isKnowledgeBase={props.isKnowledgeBase}
                isNews={state.category.type === NEWS_CATEGORY_TYPE}
                mainCount={this.props.roles.main.length}
                disableCreatePermissionsList={this.disableCreatePermissionsList}
                permissions={state.category.permissions || []}
                onUpdate={(data: any) => this.setValue('permissions', data)}
              />
            </Panel>
          )
        }
        {
          !this.isParentCategory() && !this.isNew() && state.parentCategories && state.parentCategories.length > 0 &&
            <div>
              <ButtonControl
                class='change-parent-category'
                title='Change parent category'
                onClick={() => this.setState({ showChangeParentCategoryModal: true })}
              />
              <ChangeParentCategoryModal
                changeParentCategoryError={state.changeParentCategoryError}
                isLoading={state.isLoading}
                parentCategories={state.parentCategories}
                onSubmit={this.changeParentCategory}
                onClose={() => this.setState({
                  showChangeParentCategoryModal: false,
                  changeParentCategoryError: undefined,
                  selectedParentCategory: undefined })
                }
                onSelectParentCategory={(parentCategory) => this.setState({
                  selectedParentCategory: parentCategory,
                  changeParentCategoryError: undefined })
                }
                selectedParentCategory={state.selectedParentCategory}
                showModal={state.showChangeParentCategoryModal}
              />
            </div>
        }
        <ConfirmationModal
          isLoading={state.isLoading}
          showError={state.showError}
          enableModal={state.showConfirmation}
          title={this.getDeleteConfirmationTitle()}
          message={this.getDeleteConfirmationText()}
          buttons={this.getDeleteConfirmationButtons()}
          onClose={() => this.setState({ showConfirmation: false })}
        />
      </div>
    )
  }
}

export default CategoryForm
